import React, {useEffect, useState} from 'react'
import {makeStyles, createStyles} from '@material-ui/core/styles'
import LinearProgress, {
  LinearProgressProps,
} from '@material-ui/core/LinearProgress'
import Typography, {TypographyProps} from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import styled from 'styled-components'
import Container from '@material-ui/core/Container'
import {useAttendeeVariables} from 'Event'
import {useEditMode} from 'Event/EditModeProvider'
export interface ProgressBarStyleProps {
  barColor: string
  backgroundColor: string
  textColor: string
  thickness: number
  borderRadius: number
}

export type ProgressBarProps = {
  value: number
  showing: boolean
  text: string
} & ProgressBarStyleProps

export default function ProgressBar(
  props: LinearProgressProps & ProgressBarProps,
) {
  const v = useAttendeeVariables()
  const isEditMode = useEditMode()

  if (!props.showing && !isEditMode) {
    return null
  }

  return (
    <Container maxWidth="sm">
      <Box display="flex" alignItems="center" mb={3}>
        <BarContainer thickness={props.thickness}>
          <StyledLinearProgress variant="determinate" {...props} />
          <StyledTypography
            variant="body2"
            textColor={props.textColor}
            thickness={props.thickness}
          >
            {v(props.text)}
          </StyledTypography>
        </BarContainer>
      </Box>
    </Container>
  )
}

export function ProgressBarPreview(props: Omit<ProgressBarProps, 'value'>) {
  const [progress, setProgress] = useState(10)

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) =>
        prevProgress >= 100 ? 10 : prevProgress + 10,
      )
    }, 800)
    return () => {
      clearInterval(timer)
    }
  }, [])

  return <ProgressBar {...props} value={progress} />
}
const BarContainer = styled.div<{
  thickness: number | undefined
}>`
  width: 100%;
  position: relative;
  height: ${(props) => props.thickness}px;
`

const StyledLinearProgress = styled(
  (
    props: ProgressBarStyleProps & LinearProgressProps & {showing?: boolean},
  ) => {
    // Add a delay to set the progress so that the bar animates on load
    const [innerProgress, setInnerProgress] = useState<number | undefined>(0)
    React.useEffect(() => {
      const timeout = setTimeout(() => {
        setInnerProgress(props.value)
      }, 200)
      return () => {
        clearTimeout(timeout)
      }
    }, [props.value])

    const useStylesLinerProgress = makeStyles(() =>
      createStyles({
        root: {
          height: props.thickness,
          borderRadius: props.borderRadius,
          zIndex: 0,
        },
        colorPrimary: {
          backgroundColor: props.backgroundColor,
        },
        bar: {
          borderRadius: props.borderRadius,
          backgroundColor: props.barColor,
        },
      }),
    )
    const classes = useStylesLinerProgress()
    const {
      barColor: _1,
      backgroundColor: _2,
      borderRadius: _3,
      showing: _4,
      textColor: _5,
      ...otherProps
    } = props
    return (
      <LinearProgress classes={classes} {...otherProps} value={innerProgress} />
    )
  },
)`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
`

const StyledTypography = styled(
  (
    props: Pick<ProgressBarStyleProps, 'textColor' | 'thickness'> &
      TypographyProps,
  ) => {
    const {textColor: _1, thickness: _2, ...otherProps} = props
    return <Typography {...otherProps} />
  },
)`
  position: absolute;
  left: 15px;
  top: ${(props) => props.thickness / 7}px;
  font-size: ${(props) => props.thickness / 2}px;
  color: ${(props) => props.textColor} !important;
`
